import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, ChevronRight, ChevronDown, ChevronUp, X,
  AlertTriangle, FileText, User, Phone, Clipboard,
  MessageSquare, ExternalLink, CheckCircle,
} from "lucide-react";

function ReviewModal({ alert, onClose, onReview, onResolve, onNavigate }) {
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState(null);
  const [resolveNotes, setResolveNotes] = useState("");

  const sevColors = { critical: "#fc8181", high: "#f6ad55", medium: "#ecc94b" };
  const time = alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  const date = alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleDateString() : "";

  const highlightTranscript = (text, phrase) => {
    if (!text || !phrase) return text || "No transcript available.";
    const idx = text.toLowerCase().indexOf(phrase.toLowerCase());
    if (idx === -1) return text;
    const start = Math.max(0, idx - 300);
    const end = Math.min(text.length, idx + phrase.length + 300);
    return (
      <span>
        {start > 0 && "..."}
        {text.substring(start, idx)}
        <span style={{ background: "rgba(252,129,129,0.3)", color: "#fc8181", fontWeight: 800, padding: "1px 4px", borderRadius: 3, border: "1px solid rgba(252,129,129,0.4)" }}>
          {text.substring(idx, idx + phrase.length)}
        </span>
        {text.substring(idx + phrase.length, end)}
        {end < text.length && "..."}
      </span>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}>
      <div style={{
        width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto",
        background: "linear-gradient(135deg, #0f1729, #162033)",
        border: "1px solid rgba(252,129,129,0.15)", borderRadius: 20, padding: 24,
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 16 }}>
          <div>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: `${sevColors[alert.severity] || "#ecc94b"}20`, color: sevColors[alert.severity] || "#ecc94b", textTransform: "uppercase" }}>
                {alert.severity}
              </span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(99,179,237,0.1)", color: "#63b3ed" }}>
                {alert.category || "Safety Concern"}
              </span>
              <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: alert.status === "active" ? "rgba(252,129,129,0.15)" : "rgba(246,173,85,0.1)", color: alert.status === "active" ? "#fc8181" : "#f6ad55", textTransform: "uppercase" }}>
                {alert.status}
              </span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#fc8181", fontStyle: "italic" }}>"{alert.triggerPhrase}"</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#718096" }}>
            <X size={20} />
          </button>
        </div>

        {/* Individual & Agent Info */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ padding: 12, borderRadius: 12, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.06)" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
              <User size={9} style={{ display: "inline", marginRight: 3 }} /> Individual
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#e2e8f0" }}>{alert.individualName}</div>
            <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
              {date} at {time}
            </div>
            {alert.individualId && (
              <button onClick={() => onNavigate(`/individual/${alert.individualId}`)} style={{
                display: "flex", alignItems: "center", gap: 4, marginTop: 6,
                fontSize: 10, fontWeight: 700, color: "#63b3ed", background: "none", border: "none", cursor: "pointer",
              }}>
                <ExternalLink size={9} /> View Profile
              </button>
            )}
          </div>
          <div style={{ padding: 12, borderRadius: 12, background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.06)" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Agent</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#e2e8f0" }}>{alert.agentName || "Unknown"}</div>
            <div style={{ fontSize: 10, color: "#718096", marginTop: 2 }}>
              {alert.agentLocation || "Location not recorded"}
            </div>
          </div>
        </div>

        {/* Transcript Context */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Transcript Context
          </div>
          <div style={{
            padding: 14, borderRadius: 10, background: "rgba(15,23,42,0.6)",
            border: "1px solid rgba(252,129,129,0.08)", fontSize: 12, color: "#cbd5e0",
            lineHeight: 1.7, maxHeight: 200, overflowY: "auto", fontStyle: "italic",
          }}>
            {highlightTranscript(alert.transcriptFull || alert.transcriptSnippet, alert.triggerPhrase)}
          </div>
        </div>

        {/* Alert Details Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 16 }}>
          {[
            { label: "Category", value: alert.category || "—" },
            { label: "Severity", value: alert.severity || "—" },
            { label: "Session ID", value: alert.sessionId?.substring(0, 10) || "—" },
          ].map((m, i) => (
            <div key={i} style={{ padding: "6px 10px", borderRadius: 8, background: "rgba(15,23,42,0.4)" }}>
              <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>{m.label}</div>
              <div style={{ fontSize: 11, color: "#e2e8f0", marginTop: 2, fontWeight: 600 }}>{m.value}</div>
            </div>
          ))}
        </div>

        {/* Previous review/resolution info */}
        {alert.acknowledgedBy && (
          <div style={{ fontSize: 10, color: "#f6ad55", marginBottom: 4 }}>
            Acknowledged by {alert.acknowledgedBy} · {alert.acknowledgedAt?.toDate ? alert.acknowledgedAt.toDate().toLocaleString() : ""}
          </div>
        )}
        {alert.reviewedBy && (
          <div style={{ fontSize: 10, color: "#ecc94b", marginBottom: 4 }}>
            Reviewed by {alert.reviewedBy} · {alert.reviewNotes && `"${alert.reviewNotes}"`}
          </div>
        )}

        {/* Quick Actions */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Quick Actions
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { key: "safety-plan", label: "Update Safety Plan", icon: Shield, color: "#63b3ed" },
              { key: "contact-agent", label: "Contact Agent", icon: Phone, color: "#4fd1c5" },
              { key: "document", label: "Add Documentation", icon: Clipboard, color: "#b794f4" },
              { key: "mandatory-report", label: "File Mandatory Report", icon: AlertTriangle, color: "#fc8181" },
            ].map(a => (
              <button key={a.key} onClick={() => setAction(action === a.key ? null : a.key)} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 8,
                fontSize: 10, fontWeight: 700, cursor: "pointer",
                background: action === a.key ? `${a.color}15` : "rgba(15,23,42,0.5)",
                color: action === a.key ? a.color : "#718096",
                border: `1px solid ${action === a.key ? `${a.color}30` : "rgba(99,179,237,0.06)"}`,
              }}>
                <a.icon size={11} /> {a.label}
              </button>
            ))}
          </div>

          {/* Action sub-panels */}
          {action === "safety-plan" && (
            <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "rgba(49,130,206,0.04)", border: "1px solid rgba(49,130,206,0.1)", fontSize: 11, color: "#a0aec0" }}>
              Navigate to the individual's profile to update their safety plan and risk level.
              {alert.individualId && (
                <button onClick={() => onNavigate(`/individual/${alert.individualId}`)} style={{
                  display: "block", marginTop: 6, fontSize: 10, fontWeight: 700, color: "#63b3ed",
                  background: "none", border: "none", cursor: "pointer",
                }}>
                  <ExternalLink size={9} style={{ display: "inline", marginRight: 3 }} /> Open Profile → Safety Tab
                </button>
              )}
            </div>
          )}
          {action === "contact-agent" && (
            <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "rgba(79,209,197,0.04)", border: "1px solid rgba(79,209,197,0.1)", fontSize: 11, color: "#a0aec0" }}>
              Contact {alert.agentName || "the agent"} to discuss this safety concern. Include your review notes below before marking reviewed.
            </div>
          )}
          {action === "document" && (
            <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "rgba(183,148,244,0.04)", border: "1px solid rgba(183,148,244,0.1)", fontSize: 11, color: "#a0aec0" }}>
              Add your documentation notes below. These will be saved with the alert record for audit purposes.
            </div>
          )}
          {action === "mandatory-report" && (
            <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: "rgba(252,129,129,0.04)", border: "1px solid rgba(252,129,129,0.1)" }}>
              <div style={{ fontSize: 11, color: "#fc8181", fontWeight: 700, marginBottom: 4 }}>Mandatory Reporting Reminder</div>
              <div style={{ fontSize: 10, color: "#a0aec0", lineHeight: 1.5 }}>
                If this alert involves suspected abuse, neglect, or exploitation of a vulnerable adult or child, Georgia law requires a mandatory report. Document below and file with DFCS/APS.
              </div>
            </div>
          )}
        </div>

        {/* Review Notes */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            <MessageSquare size={10} style={{ display: "inline", marginRight: 4 }} /> Review Notes
          </div>
          <textarea
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10,
              background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.08)",
              color: "#e2e8f0", fontSize: 12, outline: "none", resize: "vertical", minHeight: 80,
            }}
            placeholder="Document your review: actions taken, follow-up needed, clinical observations..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          {alert.status === "active" && (
            <button onClick={() => { onReview(alert.id, notes); onClose(); }} style={{
              flex: 1, padding: "12px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: "linear-gradient(135deg, #dd6b20, #c05621)", color: "white", border: "none", cursor: "pointer",
            }}>
              Mark Reviewed
            </button>
          )}
          {(alert.status === "active" || alert.status === "acknowledged") && (
            <button onClick={() => { onReview(alert.id, notes); onClose(); }} style={{
              flex: 1, padding: "12px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
              background: "rgba(236,201,75,0.1)", color: "#ecc94b",
              border: "1px solid rgba(236,201,75,0.15)", cursor: "pointer",
            }}>
              Review & Hold
            </button>
          )}
          <button onClick={() => { onResolve(alert.id, notes || "Resolved after review"); onClose(); }} style={{
            flex: 1, padding: "12px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
            background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer",
          }}>
            Resolve
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SafetyTab({
  C, allSafetyAlerts, teamMembers,
  handleAcknowledgeAlert, handleReviewAlert, handleResolveAlert,
}) {
  const navigate = useNavigate();
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [reviewingAlert, setReviewingAlert] = useState(null);

  const sevColors = { critical: "#fc8181", high: "#f6ad55", medium: "#ecc94b" };

  // Group alerts by agent
  const agentAlertMap = {};
  allSafetyAlerts.forEach(a => {
    const key = a.agentId || "unknown";
    if (!agentAlertMap[key]) {
      agentAlertMap[key] = { agentId: key, agentName: a.agentName || "Unknown Agent", alerts: [] };
    }
    agentAlertMap[key].alerts.push(a);
  });

  const agentAlertList = teamMembers
    .filter(m => m.role === "agent" || m.role === "counselor")
    .map(m => ({
      agentId: m.id,
      agentName: m.displayName || m.email,
      liveStatus: m.liveStatus || null,
      liveIndividual: m.liveIndividual || null,
      alerts: agentAlertMap[m.id]?.alerts || [],
    }));

  Object.values(agentAlertMap).forEach(a => {
    if (!agentAlertList.find(x => x.agentId === a.agentId)) {
      agentAlertList.push({ ...a, liveStatus: null, liveIndividual: null });
    }
  });

  agentAlertList.sort((a, b) => {
    const aActive = a.alerts.filter(x => x.status === "active").length;
    const bActive = b.alerts.filter(x => x.status === "active").length;
    if (aActive !== bActive) return bActive - aActive;
    return b.alerts.length - a.alerts.length;
  });

  const agentsWithActive = agentAlertList.filter(a => a.alerts.some(x => x.status === "active")).length;
  const agentsWithAck = agentAlertList.filter(a => a.alerts.some(x => x.status === "acknowledged") && !a.alerts.some(x => x.status === "active")).length;
  const agentsAllClear = agentAlertList.filter(a => !a.alerts.some(x => x.status === "active" || x.status === "acknowledged")).length;

  const handleOpenReview = (alert) => setReviewingAlert(alert);

  const handleReviewWithNotes = (alertId, notes) => {
    handleReviewAlert(alertId, notes);
  };

  return (
    <div>
      {/* Review Modal */}
      {reviewingAlert && (
        <ReviewModal
          alert={reviewingAlert}
          onClose={() => setReviewingAlert(null)}
          onReview={handleReviewWithNotes}
          onResolve={handleResolveAlert}
          onNavigate={(path) => { setReviewingAlert(null); navigate(path); }}
        />
      )}

      {/* Summary bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Needs Attention", value: agentsWithActive, color: agentsWithActive > 0 ? "#fc8181" : "#68d391" },
          { label: "Acknowledged", value: agentsWithAck, color: "#f6ad55" },
          { label: "All Clear", value: agentsAllClear, color: "#68d391" },
        ].map((s, i) => (
          <div key={i} style={{ ...C.card, textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{s.label} agent{s.value !== 1 ? "s" : ""}</div>
          </div>
        ))}
      </div>

      {agentAlertList.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Shield size={40} style={{ color: "rgba(104,211,145,0.2)", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 16, fontWeight: 800, color: "#4a5568" }}>All Clear</div>
          <div style={{ fontSize: 12, color: "#2d3748", marginTop: 4 }}>No agents or safety alerts.</div>
        </div>
      ) : (
        agentAlertList.map(agent => {
          const agentActive = agent.alerts.filter(a => a.status === "active");
          const agentAck = agent.alerts.filter(a => a.status === "acknowledged");
          const agentReviewed = agent.alerts.filter(a => a.status === "reviewed");
          const agentResolved = agent.alerts.filter(a => a.status === "resolved");
          const hasActive = agentActive.length > 0;
          const hasAck = agentAck.length > 0;
          const isAgentExpanded = expandedAgent === agent.agentId;
          const borderColor = hasActive ? "#fc8181" : hasAck ? "#f6ad55" : agent.alerts.length > 0 ? "#68d391" : "rgba(99,179,237,0.08)";
          const isInSession = agent.liveStatus === "in-session";

          return (
            <div key={agent.agentId} style={{ marginBottom: 10 }}>
              <div onClick={() => setExpandedAgent(isAgentExpanded ? null : agent.agentId)} style={{
                ...C.card, borderLeft: `4px solid ${borderColor}`, cursor: "pointer",
                background: hasActive ? "rgba(252,129,129,0.03)" : C.card.background,
                marginBottom: 0, borderRadius: isAgentExpanded ? "12px 12px 0 0" : 12,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: hasActive ? "rgba(252,129,129,0.1)" : hasAck ? "rgba(246,173,85,0.1)" : "rgba(104,211,145,0.1)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, fontWeight: 900,
                      color: hasActive ? "#fc8181" : hasAck ? "#f6ad55" : "#68d391",
                    }}>
                      {(agent.agentName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>{agent.agentName}</div>
                      <div style={{ fontSize: 10, color: "#718096", marginTop: 1 }}>
                        {isInSession && (
                          <span style={{ color: "#4fd1c5", fontWeight: 700 }}>
                            ● In Session{agent.liveIndividual ? ` with ${agent.liveIndividual}` : ""} &nbsp;
                          </span>
                        )}
                        {agent.alerts.length === 0 ? "No alerts" : `${agent.alerts.length} alert${agent.alerts.length > 1 ? "s" : ""}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {agentActive.length > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 8, background: "rgba(252,129,129,0.15)", color: "#fc8181" }}>{agentActive.length} active</span>
                    )}
                    {agentAck.length > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: "rgba(246,173,85,0.1)", color: "#f6ad55" }}>{agentAck.length} ack</span>
                    )}
                    {!hasActive && !hasAck && agent.alerts.length > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 8, background: "rgba(104,211,145,0.1)", color: "#68d391" }}>✓ clear</span>
                    )}
                    <ChevronRight size={14} style={{ color: "#4a5568", transform: isAgentExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }} />
                  </div>
                </div>
              </div>

              {/* Expanded alerts */}
              {isAgentExpanded && (
                <div style={{ background: "rgba(15,23,42,0.3)", border: "1px solid rgba(99,179,237,0.06)", borderTop: "none", borderRadius: "0 0 12px 12px", padding: 12 }}>
                  {agent.alerts.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "20px 0", fontSize: 12, color: "#4a5568" }}>No alerts for this agent.</div>
                  ) : (
                    <>
                      {/* Active */}
                      {agentActive.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: "#fc8181", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Active ({agentActive.length})</div>
                          {agentActive.map(alert => {
                            const time = alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
                            const date = alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleDateString() : "";
                            return (
                              <div key={alert.id} style={{ ...C.card, borderLeft: "3px solid #fc8181", marginBottom: 8, background: "rgba(252,129,129,0.02)" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                                      <span style={{ fontSize: 9, fontWeight: 800, textTransform: "uppercase", padding: "2px 6px", borderRadius: 5, background: `${sevColors[alert.severity] || "#ecc94b"}20`, color: sevColors[alert.severity] || "#ecc94b" }}>{alert.severity}</span>
                                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 5, background: "rgba(99,179,237,0.1)", color: "#63b3ed" }}>{alert.category || "Safety"}</span>
                                    </div>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: "#fc8181", fontStyle: "italic", marginBottom: 4 }}>"{alert.triggerPhrase}"</div>
                                    <div style={{ fontSize: 12, fontWeight: 700 }}>{alert.individualName}</div>
                                    <div style={{ fontSize: 10, color: "#718096" }}>{date} at {time}{alert.agentLocation ? ` · ${alert.agentLocation}` : ""}</div>
                                    {alert.transcriptSnippet && (
                                      <div style={{ marginTop: 6, padding: 6, borderRadius: 6, background: "rgba(15,23,42,0.5)", fontSize: 10, color: "#a0aec0", fontStyle: "italic", lineHeight: 1.5, maxHeight: 36, overflow: "hidden" }}>
                                        ...{alert.transcriptSnippet.substring(0, 100)}...
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: 10, minWidth: 100 }} onClick={e => e.stopPropagation()}>
                                    <button style={{ padding: "6px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: "linear-gradient(135deg, #dd6b20, #c05621)", color: "white", border: "none", cursor: "pointer" }}
                                      onClick={() => handleAcknowledgeAlert(alert.id)}>Acknowledge</button>
                                    <button style={{ padding: "6px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: "rgba(236,201,75,0.1)", color: "#ecc94b", border: "1px solid rgba(236,201,75,0.15)", cursor: "pointer" }}
                                      onClick={() => handleOpenReview(alert)}>Review</button>
                                    <button style={{ padding: "6px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer" }}
                                      onClick={() => handleResolveAlert(alert.id, "Resolved from dashboard")}>Resolve</button>
                                    {alert.individualId && (
                                      <button style={{ padding: "6px 10px", borderRadius: 8, fontSize: 10, fontWeight: 700, background: "rgba(99,179,237,0.08)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.12)", cursor: "pointer" }}
                                        onClick={() => navigate(`/individual/${alert.individualId}`)}>Profile</button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Acknowledged */}
                      {agentAck.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: "#f6ad55", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Acknowledged ({agentAck.length})</div>
                          {agentAck.map(alert => (
                            <div key={alert.id} style={{ ...C.card, borderLeft: "3px solid #f6ad55", marginBottom: 6, padding: "10px 14px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#f6ad55", fontStyle: "italic" }}>"{alert.triggerPhrase}"</div>
                                  <div style={{ fontSize: 11 }}>{alert.individualName} · <span style={{ color: "#718096" }}>{alert.createdAt?.toDate ? alert.createdAt.toDate().toLocaleDateString() : ""}</span></div>
                                </div>
                                <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                                  <button style={{ padding: "5px 8px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: "rgba(236,201,75,0.08)", color: "#ecc94b", border: "1px solid rgba(236,201,75,0.12)", cursor: "pointer" }}
                                    onClick={() => handleOpenReview(alert)}>Review</button>
                                  <button style={{ padding: "5px 8px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer" }}
                                    onClick={() => handleResolveAlert(alert.id, "Resolved")}>Resolve</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reviewed */}
                      {agentReviewed.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: "#ecc94b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Reviewed ({agentReviewed.length})</div>
                          {agentReviewed.map(alert => (
                            <div key={alert.id} style={{ ...C.card, borderLeft: "3px solid #ecc94b", marginBottom: 6, padding: "10px 14px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#ecc94b", fontStyle: "italic" }}>"{alert.triggerPhrase}"</div>
                                  <div style={{ fontSize: 11 }}>{alert.individualName}</div>
                                  {alert.reviewNotes && (
                                    <div style={{ fontSize: 10, color: "#a0aec0", marginTop: 4, fontStyle: "italic" }}>
                                      Note: {alert.reviewNotes}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 9, color: "#718096", marginTop: 2 }}>
                                    Reviewed by {alert.reviewedBy} · {alert.reviewedAt?.toDate ? alert.reviewedAt.toDate().toLocaleDateString() : ""}
                                  </div>
                                </div>
                                <div onClick={e => e.stopPropagation()}>
                                  <button style={{ padding: "5px 8px", borderRadius: 6, fontSize: 9, fontWeight: 700, background: "linear-gradient(135deg, #38a169, #2f855a)", color: "white", border: "none", cursor: "pointer" }}
                                    onClick={() => handleResolveAlert(alert.id, alert.reviewNotes || "Resolved after review")}>Resolve</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Resolved summary */}
                      {agentResolved.length > 0 && (
                        <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(104,211,145,0.04)", border: "1px solid rgba(104,211,145,0.08)" }}>
                          <div style={{ fontSize: 10, color: "#68d391", fontWeight: 600 }}>
                            {agentResolved.length} resolved
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}